/**
 * Location Service Engine — what this tenant has switched on, read from ``tenant.capabilities`` (resolved by the
 * server: master gate + module dependencies; absent for every tenant without the engine). The UI only ever asks
 * "is module X on?"; the server enforces the same switches on every endpoint, so hiding here is convenience, not
 * security.
 */
import { useTenant } from "../contexts/TenantContext";

export const LSE_KEY = "location_service_engine";

export function lseModules(tenant) {
    const g = tenant?.capabilities?.[LSE_KEY];
    return new Set(g?.enabled ? g.modules || [] : []);
}

export function lseOn(tenant, module) {
    const mods = lseModules(tenant);
    if (!module) return !!tenant?.capabilities?.[LSE_KEY]?.enabled;
    return mods.has(module);
}

export function useLse() {
    const { tenant } = useTenant();
    const mods = lseModules(tenant);
    return { on: !!tenant?.capabilities?.[LSE_KEY]?.enabled, has: (m) => mods.has(m), modules: mods, tenant };
}

/** Adds the customer's delivery choice to a ``/cart`` query (no-op without a choice). */
export function deliveryParams(choice, qs) {
    if (!choice?.mode) return qs;
    qs.set("delivery_mode", choice.mode);
    if (choice.date) qs.set("delivery_date", choice.date);
    if (choice.slot_id) qs.set("delivery_slot_id", choice.slot_id);
    return qs;
}

/** Fields for the place-order payload — empty object without a choice, so other tenants' payloads are unchanged. */
export function deliveryPayload(choice) {
    if (!choice?.mode) return {};
    return {
        delivery_mode: choice.mode,
        ...(choice.date ? { delivery_date: choice.date } : {}),
        ...(choice.slot_id ? { delivery_slot_id: choice.slot_id } : {}),
    };
}

/** True when the server says a delivery option still has to be picked (or the pick isn't valid). */
export function deliveryBlocking(lse) {
    if (!lse) return null;
    const hard = (lse.issues || []).find((i) => i.code !== "CHOOSE_DELIVERY");
    if (hard) return hard.message;
    if ((lse.issues || []).some((i) => i.code === "CHOOSE_DELIVERY")) return "Choose a delivery option";
    return null;
}

export const ZONE_TYPE_LABEL = {
    URBAN: "Urban", SEMI_URBAN: "Semi-urban", RURAL: "Rural", COMING_SOON: "Coming soon", UNAVAILABLE: "Unavailable",
};

export const inr = (v) => {
    const n = Math.round((Number(v) || 0) * 100) / 100;
    const digits = Number.isInteger(n) ? 0 : 2;
    const s = Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });
    return `${n < 0 ? "−" : ""}₹${s}`;
};
